// ABOUTME: Pennant InstancedMesh primitives — pole + rectangular flag with team colors
// ABOUTME: Pure mesh module. Scene wiring lives elsewhere (hopp-54x interaction integration)

import * as THREE from 'three';

// Pennant world dimensions (units = approx 1 unit per pennant footprint)
export const POLE_HEIGHT = 3.0;
export const POLE_RADIUS = 0.028;
export const POLE_RADIAL_SEGMENTS = 12;
// Constant world-unit gap between the flag's top hem and the pole top. The flag
// sits flush at the pole top, so this is zero; kept as a named constant for the
// call sites and tests that reference the clearance.
export const FLAG_FINIAL_CLEARANCE = 0; // flag top sits flush at the pole top
const FLAG_LENGTH = 1.56;    // horizontal extent from pole to right edge (1.2 × 1.3)
const FLAG_HEIGHT = 1.3;     // vertical extent (constant — rectangular; 1.0 × 1.3)
const FLAG_TOP_Y = POLE_HEIGHT;                   // top edge sits at pole top
const FLAG_BOTTOM_Y = POLE_HEIGHT - FLAG_HEIGHT;  // bottom edge of flag
export const PENNANT_FLAG_FOCUS_OFFSET_X = FLAG_LENGTH / 2;
export const PENNANT_FLAG_FOCUS_Y = FLAG_BOTTOM_Y + FLAG_HEIGHT / 2;
export const PENNANT_FLAG_GRID_COLS = 8;
export const PENNANT_FLAG_GRID_ROWS = 4;
export const LEADING_TEAM_HEIGHT_SCALE = 1.35;
export const TEAM_FLAG_WIDTH_GROWTH_FACTOR = 0.38;

// Geometry constants exposed for tests
export const PENNANT_FLAG_VERTEX_COUNT = PENNANT_FLAG_GRID_COLS * PENNANT_FLAG_GRID_ROWS;
export const PENNANT_FLAG_INDEX_COUNT = (PENNANT_FLAG_GRID_COLS - 1) * (PENNANT_FLAG_GRID_ROWS - 1) * 6;
export const PENNANT_SHADOW_VERTEX_COUNT = 4;
export const PENNANT_SHADOW_INDEX_COUNT = 6;
export const PRELOADED_PENNANT_MAX_SHADER_AGE_SECONDS = 30;
export const TEAM_HEIGHT_TIER_SCALES = Object.freeze([1.35, 1.22, 1.12]);

const SHADOW_Y_OFFSET = 0.012;
const SHADOW_MIN_X = -0.22;
const SHADOW_MAX_X = 0.98;
const SHADOW_HALF_WIDTH = 0.34;
const SHADOW_MIN_ELEVATION_Y = 0.16;
const SHADOW_MIN_LENGTH_SCALE = 1.0;
const SHADOW_MAX_LENGTH_SCALE = 2.8;

const HEX_RE = /^#?([0-9a-f]{6})$/i;

export function hexToRgb(hex) {
    if (typeof hex !== 'string') {
        throw new Error(`hexToRgb: expected string, got ${typeof hex}`);
    }
    const match = hex.match(HEX_RE);
    if (!match) {
        throw new Error(`hexToRgb: not a valid 6-digit hex colour: ${hex}`);
    }
    const value = match[1];
    const r = parseInt(value.slice(0, 2), 16) / 255;
    const g = parseInt(value.slice(2, 4), 16) / 255;
    const b = parseInt(value.slice(4, 6), 16) / 255;
    return [r, g, b];
}

/**
 * Build the per-vertex position/uv/index arrays for a rectangular pennant flag.
 *
 * Layout: 8 columns × 4 rows of vertices, with all rows at full width. The
 * extra subdivisions let the flutter shader add sag/curl/fold detail while
 * keeping a single instanced draw call.
 *
 * Rectangular shape (vs. the previous triangular tip) avoids texture
 * tip-crush once flag images are sampled by the fragment shader.
 */
export function buildFlagGeometryDescriptor() {
    const positions = new Float32Array(PENNANT_FLAG_VERTEX_COUNT * 3);
    const uvs = new Float32Array(PENNANT_FLAG_VERTEX_COUNT * 2);

    for (let row = 0; row < PENNANT_FLAG_GRID_ROWS; row++) {
        const v = 1 - row / (PENNANT_FLAG_GRID_ROWS - 1);
        const y = FLAG_BOTTOM_Y + v * FLAG_HEIGHT;

        for (let col = 0; col < PENNANT_FLAG_GRID_COLS; col++) {
            const u = col / (PENNANT_FLAG_GRID_COLS - 1);
            const x = u * FLAG_LENGTH;
            const idx = row * PENNANT_FLAG_GRID_COLS + col;

            positions[idx * 3 + 0] = x;
            positions[idx * 3 + 1] = y;
            positions[idx * 3 + 2] = 0;
            uvs[idx * 2 + 0] = u;
            uvs[idx * 2 + 1] = v;
        }
    }

    const indices = [];
    for (let row = 0; row < PENNANT_FLAG_GRID_ROWS - 1; row++) {
        for (let col = 0; col < PENNANT_FLAG_GRID_COLS - 1; col++) {
            const tl = row * PENNANT_FLAG_GRID_COLS + col;
            const tr = tl + 1;
            const bl = (row + 1) * PENNANT_FLAG_GRID_COLS + col;
            const br = bl + 1;
            indices.push(tl, bl, tr, tr, bl, br);
        }
    }

    return { positions, uvs, indices };
}

export function buildShadowGeometryDescriptor() {
    return {
        positions: new Float32Array([
            SHADOW_MIN_X, SHADOW_Y_OFFSET, -SHADOW_HALF_WIDTH,
            SHADOW_MIN_X, SHADOW_Y_OFFSET,  SHADOW_HALF_WIDTH,
            SHADOW_MAX_X, SHADOW_Y_OFFSET, -SHADOW_HALF_WIDTH,
            SHADOW_MAX_X, SHADOW_Y_OFFSET,  SHADOW_HALF_WIDTH,
        ]),
        uvs: new Float32Array([
            0, 0,
            0, 1,
            1, 0,
            1, 1,
        ]),
        indices: [0, 1, 2, 2, 1, 3],
    };
}

function buildFlagGeometry() {
    const desc = buildFlagGeometryDescriptor();
    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(desc.indices);
    geometry.setAttribute('position', new THREE.BufferAttribute(desc.positions, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(desc.uvs, 2));
    geometry.computeVertexNormals();
    return geometry;
}

function buildPoleGeometry() {
    const g = new THREE.CylinderGeometry(
        POLE_RADIUS * 0.72,
        POLE_RADIUS * 0.92,
        POLE_HEIGHT,
        POLE_RADIAL_SEGMENTS,
        1,
        false,
    );
    // Cylinder is centred at origin; lift so the bottom sits at y=0.
    g.translate(0, POLE_HEIGHT / 2, 0);
    g.computeVertexNormals();
    return g;
}

function buildBeaconGeometry() {
    // Unit quad (x in [-0.5, 0.5], y in [0, 1]); the vertex shader billboards it
    // to face the camera and scales it to the beacon size, anchored at the base.
    const g = new THREE.PlaneGeometry(1, 1);
    g.translate(0, 0.5, 0);
    return g;
}

function buildShadowGeometry() {
    const desc = buildShadowGeometryDescriptor();
    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(desc.indices);
    geometry.setAttribute('position', new THREE.BufferAttribute(desc.positions, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(desc.uvs, 2));
    return geometry;
}

// Flutter constants — tuned by ear in test-pennant.html, refine in hopp-b2f if needed
const BURST_DECAY_SECONDS = 0.5;       // exp decay constant; ~1.5s perceptual burst window
const BURST_AMPLITUDE_MULT = 3.0;      // creation burst is 3× ambient amplitude
const AMBIENT_FREQ_X = 8.0;            // spatial frequency along pennant length
const AMBIENT_FREQ_TIME = 6.0;         // temporal frequency (Hz-ish)
const AMBIENT_PHASE_SCATTER = 7.13;    // per-instance phase offset to avoid synchrony
const FLUTTER_BASE_AMPLITUDE = 0.06;   // world units of displacement at uv.x = 1

// Fresh-pole glow — a pole planted within this window emits a warm pulsing halo
// that fades to nothing as it ages out. Gives a lone 3am fan the sense that
// others are planting alongside them right now. Driven by the authoritative
// server creation time so other fans' fresh poles glow too and it survives
// reloads (see aPolePlantedAt wiring in addPennant / updateFlutter).
export const FRESH_GLOW_WINDOW_SECONDS = 1800; // 30 minutes
const FRESH_GLOW_COLOR = [1.0, 0.78, 0.46];    // warm amber / warm-white
const FRESH_GLOW_PULSE_SPEED = 2.4;            // gentle breathing (~2.6s period)
const FRESH_GLOW_STRENGTH = 1.3;               // additive halo intensity at peak

// Fresh-pole sky beacon — a warm light shaft rising from a freshly planted pole
// into the sky so new plants read from anywhere (the "someone just joined at
// 3am" cue). Camera-facing additive billboard, fading with the same window as
// the pole halo.
const BEACON_HEIGHT = 40;          // world units the shaft rises into the sky
const BEACON_WIDTH = 7.5;          // shaft width scale (flares wider toward the top)
const BEACON_PULSE_SPEED = 1.7;    // gentle breathing

const BEACON_VERTEX_SHADER = /* glsl */ `
    uniform float uTime;
    uniform float uBeaconHeight;
    uniform float uBeaconWidth;
    attribute float aPolePlantedAt;
    varying float vAge;
    varying vec2 vQuad;

    void main() {
        vQuad = vec2(position.x, position.y);
        vAge = uTime - aPolePlantedAt;
        // Billboard a vertical shaft at the pole base: face the camera around the
        // world-up axis, so the column always reads edge-on-free and stays upright.
        vec4 base = modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
        vec3 toCam = cameraPosition - base.xyz;
        vec3 right = normalize(vec3(-toCam.z, 0.0, toCam.x));
        // A near-upright shaft (only a slight upward flare) wide enough at the
        // base to hold the whole pennant, standing ~vertical to the ground.
        float w = uBeaconWidth * (0.85 + 0.4 * position.y);
        vec3 worldPos = base.xyz
            + right * (position.x * w)
            + vec3(0.0, 1.0, 0.0) * (position.y * uBeaconHeight);
        gl_Position = projectionMatrix * viewMatrix * vec4(worldPos, 1.0);
    }
`;

const BEACON_FRAGMENT_SHADER = /* glsl */ `
    uniform vec3 uBeaconColor;
    uniform float uFreshGlowWindow;
    uniform float uBeaconPulseSpeed;
    uniform float uTime;
    varying float vAge;
    varying vec2 vQuad;

    void main() {
        float fade = 1.0 - smoothstep(0.0, uFreshGlowWindow, vAge);
        if (fade <= 0.002 || vAge < 0.0) discard;
        // Bright at the base, feathering to nothing at the top of the shaft.
        float vert = pow(1.0 - vQuad.y, 1.6);
        // Soft horizontal falloff → a feathered column rather than a hard bar.
        float horiz = 1.0 - smoothstep(0.0, 1.0, abs(vQuad.x) * 2.0);
        float pulse = 0.9 + 0.1 * sin(uTime * uBeaconPulseSpeed);
        gl_FragColor = vec4(uBeaconColor, vert * horiz * fade * pulse * 0.45);
    }
`;

/**
 * Warm-glow fade for a pole of the given age in seconds. Returns ~1.0 for a
 * just-planted pole, eases toward 0.0 as it reaches the window, and stays 0
 * beyond it. Pure — mirrors the smoothstep falloff in POLE_FRAGMENT_SHADER so
 * the intended curve is unit-testable without a GL context.
 */
export function poleFreshGlowFade(ageSeconds, windowSeconds = FRESH_GLOW_WINDOW_SECONDS) {
    if (!Number.isFinite(ageSeconds) || !(windowSeconds > 0)) return 0;
    const t = Math.min(Math.max(ageSeconds / windowSeconds, 0), 1);
    const smooth = t * t * (3 - 2 * t); // smoothstep(0, 1, t)
    return 1 - smooth;
}

// Shared lighting snippet — sun-direction Lambert + sky-dome ambient,
// matching the daylight pitch shader. Pennants are double-sided so the
// flag uses abs(dot(N, L)) to keep both faces lit.
const LIGHTING_GLSL = /* glsl */ `
    uniform vec3 uSunDirection;
    uniform vec3 uSunColor;
    uniform vec3 uSkyAmbient;
    uniform float uSunIntensity;
    uniform float uSkyIntensity;

    vec3 computeLighting(vec3 worldNormal, bool doubleSided) {
        vec3 N = normalize(worldNormal);
        float diffuse = doubleSided
            ? abs(dot(N, uSunDirection))
            : max(dot(N, uSunDirection), 0.0);
        return uSunColor * diffuse * uSunIntensity
             + uSkyAmbient * uSkyIntensity;
    }
`;

const FLAG_VERTEX_SHADER = /* glsl */ `
    uniform float uTime;
    uniform float uFlutterIntensity;
    uniform float uBurstDecaySeconds;
    uniform float uBurstAmplitudeMult;
    uniform float uAmbientFreqX;
    uniform float uAmbientFreqTime;
    uniform float uAmbientPhaseScatter;
    uniform float uFlutterBaseAmplitude;

    attribute vec3 aPrimary;
    attribute float aPlantedAt;
    attribute float aFlagIndex;
    attribute float aPhase;
    attribute float aSwingBoost;
    varying vec3 vColor;
    varying float vUvX;
    varying vec2 vUv;
    varying float vFlagIndex;
    varying vec3 vWorldNormal;
    varying float vFoldShade;
    varying float vFreeEdge;

    void main() {
        vColor = aPrimary;
        vUvX = uv.x;
        vUv = uv;
        vFlagIndex = aFlagIndex;

        float phase = aPhase * uAmbientPhaseScatter;
        float age = max(uTime - aPlantedAt, 0.0);
        float burst = exp(-age / uBurstDecaySeconds);
        float amplitude = uFlutterIntensity * uFlutterBaseAmplitude *
                          (1.0 + burst * uBurstAmplitudeMult) *
                          (1.0 + aSwingBoost * 2.0);

        float pinned = smoothstep(0.03, 0.32, uv.x);
        float freeEdge = uv.x * uv.x;
        float verticalBelly = 1.0 - abs(uv.y * 2.0 - 1.0);
        float waveA = sin(position.x * uAmbientFreqX + uTime * uAmbientFreqTime + phase);
        float waveB = sin(position.x * 17.0 + uv.y * 7.0 + uTime * 3.1 + phase * 0.43);
        float displacement = (waveA + waveB * 0.32) * amplitude * pinned * (0.62 + verticalBelly * 0.38);
        float sag = freeEdge * (1.0 - uv.y) * 0.055;
        float curl = sin(uv.y * 3.14159265 + uTime * 1.4 + phase) * 0.035 * freeEdge;

        vec3 displacedPosition = position;
        displacedPosition.z += displacement;
        displacedPosition.z += curl;
        displacedPosition.y += displacement * 0.16 - sag;
        displacedPosition.x -= freeEdge * (1.0 - uv.y) * 0.025;

        mat4 worldMatrix = modelMatrix * instanceMatrix;
        vec4 worldPos = worldMatrix * vec4(displacedPosition, 1.0);
        float slope = cos(position.x * uAmbientFreqX + uTime * uAmbientFreqTime + phase)
                    * uAmbientFreqX * amplitude * pinned;
        vec3 clothNormal = normalize(vec3(-slope, 0.08 * freeEdge, 1.0));
        vWorldNormal = mat3(worldMatrix) * clothNormal;
        vFoldShade = waveA * 0.5 + waveB * 0.18 + 0.5;
        vFreeEdge = freeEdge;

        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

const FLAG_FRAGMENT_SHADER = /* glsl */ `
${LIGHTING_GLSL}
    uniform sampler2D uAtlas;
    uniform vec2 uAtlasGrid;
    uniform float uHasAtlas;
    uniform vec2 uCellPadding;
    uniform float uFabricStrength;
    uniform float uSeamStrength;
    uniform float uFoldShadeStrength;

    varying vec3 vColor;
    varying float vUvX;
    varying vec2 vUv;
    varying float vFlagIndex;
    varying vec3 vWorldNormal;
    varying float vFoldShade;
    varying float vFreeEdge;

    float hash12(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
    }

    float fabricNoise(vec2 uv) {
        float warp = sin(uv.x * 240.0);
        float weft = sin(uv.y * 190.0);
        float fine = hash12(floor(uv * vec2(110.0, 70.0)));
        return warp * weft * 0.5 + fine * 0.35;
    }

    void main() {
        // Atlas cell origin: simple row-major math, then row-flip to match
        // CanvasTexture's default flipY convention (canvas top-row sits high
        // in v-space).
        vec2 cellSize = 1.0 / uAtlasGrid;
        float col = mod(vFlagIndex, uAtlasGrid.x);
        float row = (uAtlasGrid.y - 1.0) - floor(vFlagIndex / uAtlasGrid.x);
        vec2 cellOrigin = vec2(col, row) * cellSize;
        vec2 paddedUv = uCellPadding + vUv * (1.0 - 2.0 * uCellPadding);
        vec3 atlasColour = texture2D(uAtlas, cellOrigin + paddedUv * cellSize).rgb;
        vec3 base = mix(vColor, atlasColour, uHasAtlas);

        float edgeDistance = min(min(vUv.x, 1.0 - vUv.x), min(vUv.y, 1.0 - vUv.y));
        float seamMask = 1.0 - smoothstep(0.015, 0.075, edgeDistance);
        float weave = fabricNoise(vUv);
        float clothGrain = 1.0 + (weave - 0.35) * uFabricStrength;
        float foldShade = 1.0 + (vFoldShade - 0.5) * uFoldShadeStrength;
        float freeEdgeHighlight = smoothstep(0.62, 1.0, vFreeEdge) * 0.035;

        base *= clothGrain * foldShade;
        base = mix(base, base * 0.68, seamMask * uSeamStrength);
        base += vec3(freeEdgeHighlight);
        if (!gl_FrontFacing) {
            base = mix(base * 0.82, vec3(1.0), 0.035);
        }

        vec3 lighting = computeLighting(vWorldNormal, true);
        gl_FragColor = vec4(base * lighting, 1.0);
    }
`;

const SHADOW_VERTEX_SHADER = /* glsl */ `
    varying vec2 vUv;

    void main() {
        vUv = uv;
        mat4 worldMatrix = modelMatrix * instanceMatrix;
        vec4 worldPos = worldMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

const SHADOW_FRAGMENT_SHADER = /* glsl */ `
    uniform float uShadowOpacity;
    varying vec2 vUv;

    float ellipseMask(vec2 p, vec2 center, vec2 radius, float softness) {
        float d = length((p - center) / radius);
        return 1.0 - smoothstep(1.0 - softness, 1.0, d);
    }

    void main() {
        float poleBase = ellipseMask(vUv, vec2(0.18, 0.50), vec2(0.13, 0.18), 0.55);
        float flagCast = ellipseMask(vUv, vec2(0.58, 0.50), vec2(0.48, 0.31), 0.82);
        float taper = 1.0 - smoothstep(0.15, 1.0, vUv.x);
        float alpha = (poleBase * 0.48 + flagCast * taper * 0.30) * uShadowOpacity;
        if (alpha < 0.006) discard;
        gl_FragColor = vec4(0.01, 0.025, 0.012, alpha);
    }
`;

const POLE_VERTEX_SHADER = /* glsl */ `
    uniform float uPoleHeight;
    attribute float aPolePlantedAt;
    varying vec3 vWorldNormal;
    varying vec3 vViewDir;
    varying float vAxial;
    varying float vPlantedAt;

    void main() {
        mat4 worldMatrix = modelMatrix * instanceMatrix;
        vec4 worldPos = worldMatrix * vec4(position, 1.0);
        vWorldNormal = normalize(mat3(worldMatrix) * normal);
        vViewDir = normalize(cameraPosition - worldPos.xyz);
        vAxial = clamp(position.y / uPoleHeight, 0.0, 1.0);
        vPlantedAt = aPolePlantedAt;
        gl_Position = projectionMatrix * viewMatrix * worldPos;
    }
`;

const POLE_FRAGMENT_SHADER = /* glsl */ `
${LIGHTING_GLSL}
    uniform vec3 uPoleWhite;
    uniform vec3 uPoleShade;
    uniform vec3 uPoleHighlight;
    uniform float uSpecularStrength;
    uniform float uRimStrength;
    uniform float uTime;
    uniform vec3 uFreshGlowColor;
    uniform float uFreshGlowWindow;
    uniform float uFreshGlowPulseSpeed;
    uniform float uFreshGlowStrength;

    varying vec3 vWorldNormal;
    varying vec3 vViewDir;
    varying float vAxial;
    varying float vPlantedAt;

    void main() {
        vec3 N = normalize(vWorldNormal);
        vec3 V = normalize(vViewDir);
        vec3 L = normalize(uSunDirection);
        vec3 H = normalize(L + V);

        float diffuse = max(dot(N, L), 0.0);
        float specular = pow(max(dot(N, H), 0.0), 64.0) * uSpecularStrength;
        float rim = pow(1.0 - max(abs(dot(N, V)), 0.0), 2.2) * uRimStrength;
        float sideShade = 0.82 + 0.18 * abs(N.x);
        float axialBand = 0.985 + 0.015 * sin(vAxial * 64.0);

        vec3 color = mix(uPoleShade, uPoleWhite, 0.72 + diffuse * 0.18);
        color *= sideShade * axialBand;
        color += uPoleHighlight * (specular * 0.48 + rim * 0.16);

        // Fresh-pole glow: a freshly planted pole warms toward amber and emits a
        // gently pulsing halo that fades to nothing as it ages past
        // uFreshGlowWindow (mirrors poleFreshGlowFade()). The rim term weights
        // the emissive toward the pole's silhouette so it reads as a halo. This
        // is the "not alone at 3am" cue.
        float age = uTime - vPlantedAt;
        float fade = 1.0 - smoothstep(0.0, uFreshGlowWindow, age);
        float pulse = 0.72 + 0.28 * sin(uTime * uFreshGlowPulseSpeed);
        float glowAmt = fade * pulse;
        color = mix(color, uFreshGlowColor, glowAmt * 0.42);
        color += uFreshGlowColor * (glowAmt * uFreshGlowStrength) * (0.5 + rim * 1.7);

        gl_FragColor = vec4(color, 1.0);
    }
`;

// Default sun direction used only when tests or standalone demos do not pass
// the field scene's current-time sun vector.
const DEFAULT_SUN_DIRECTION = new THREE.Vector3(0.3, 0.85, -0.45).normalize();

function normalizeSunDirection(sunDirection) {
    return sunDirection
        ? new THREE.Vector3(sunDirection.x, sunDirection.y, sunDirection.z).normalize()
        : DEFAULT_SUN_DIRECTION.clone();
}

export function deriveShadowFromSun(sunDirection) {
    const direction = normalizeSunDirection(sunDirection);
    const groundLength = Math.hypot(direction.x, direction.z);
    const castX = -direction.x;
    const castZ = -direction.z;
    const yaw = groundLength > 1e-4 ? Math.atan2(-castZ, castX) : 0;
    const elevationY = THREE.MathUtils.clamp(direction.y, SHADOW_MIN_ELEVATION_Y, 1);
    const slope = groundLength / elevationY;

    return {
        direction,
        yaw,
        lengthScale: THREE.MathUtils.clamp(
            0.9 + slope * 0.55,
            SHADOW_MIN_LENGTH_SCALE,
            SHADOW_MAX_LENGTH_SCALE,
        ),
        widthScale: THREE.MathUtils.clamp(0.96 + slope * 0.05, 0.98, 1.18),
        opacity: THREE.MathUtils.clamp(0.22 + elevationY * 0.30, 0.25, 0.48),
    };
}

// 1×1 white texture stand-in so the flag shader compiles and renders before
// the real atlas finishes loading. uHasAtlas = 0 keeps it out of the visual
// (mix() falls back to vColor) — but a sampler2D uniform must reference a
// real texture or some drivers complain.
function createPlaceholderAtlasTexture() {
    const tex = new THREE.DataTexture(
        new Uint8Array([255, 255, 255, 255]),
        1, 1, THREE.RGBAFormat
    );
    tex.needsUpdate = true;
    return tex;
}

const ATLAS_DEFAULT_GRID = new THREE.Vector2(8, 6);
const ATLAS_DEFAULT_PADDING_UV = new THREE.Vector2(2 / 256, 2 / 170);

function buildLightingUniforms(sunDirection) {
    const dir = normalizeSunDirection(sunDirection);
    return {
        uSunDirection: { value: dir },
        uSunColor:     { value: new THREE.Color(0xfff5e0) },
        uSkyAmbient:   { value: new THREE.Color(0xb8d0e8) },
        uSunIntensity: { value: 1.0 },
        uSkyIntensity: { value: 0.7 },
    };
}

/**
 * Create a pennant rendering system with two InstancedMeshes (pole + flag)
 * sharing instance count and matrices. Add both meshes to the scene; manage
 * placement via `addPennant`, `updatePennant`, `clearPennant`.
 */
export function createPennantSystem({ maxInstances = 5000, sunDirection = null, flagAtlas = null, freshGlowWindowSeconds = FRESH_GLOW_WINDOW_SECONDS } = {}) {
    const flagGeometry = buildFlagGeometry();
    const poleGeometry = buildPoleGeometry();
    const shadowGeometry = buildShadowGeometry();
    const beaconGeometry = buildBeaconGeometry();
    const shadowFromSun = deriveShadowFromSun(sunDirection);

    // Per-instance attributes — initialized to zeros, written via addPennant
    const aPrimary = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 3), 3);
    const aSecondary = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances * 3), 3);
    const aPlantedAt = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances), 1);
    const aFlagIndex = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances), 1);
    const aPhase = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances), 1);
    const aSwingBoost = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances), 1);
    // Un-clamped planted-at (seconds since page load) for the pole's fresh-glow
    // age. Distinct from the flag's aPlantedAt, which is clamped to a ~30s
    // creation burst — the glow needs the true wall-clock age up to 30 min.
    const aPolePlantedAt = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances), 1);
    // The beacon has its OWN planted-at buffer (not the pole's) so its instances
    // can be compacted down to just the currently-fresh poles — see rebuildBeacons.
    const aBeaconPlantedAt = new THREE.InstancedBufferAttribute(new Float32Array(maxInstances), 1);
    aBeaconPlantedAt.setUsage(THREE.DynamicDrawUsage);
    aPrimary.setUsage(THREE.DynamicDrawUsage);
    aSecondary.setUsage(THREE.DynamicDrawUsage);
    aPlantedAt.setUsage(THREE.DynamicDrawUsage);
    aFlagIndex.setUsage(THREE.DynamicDrawUsage);
    aPhase.setUsage(THREE.DynamicDrawUsage);
    aSwingBoost.setUsage(THREE.DynamicDrawUsage);
    aPolePlantedAt.setUsage(THREE.DynamicDrawUsage);

    flagGeometry.setAttribute('aPrimary', aPrimary);
    flagGeometry.setAttribute('aPlantedAt', aPlantedAt);
    flagGeometry.setAttribute('aFlagIndex', aFlagIndex);
    flagGeometry.setAttribute('aPhase', aPhase);
    flagGeometry.setAttribute('aSwingBoost', aSwingBoost);
    poleGeometry.setAttribute('aSecondary', aSecondary);
    poleGeometry.setAttribute('aPolePlantedAt', aPolePlantedAt);
    // Beacon uses its own compacted planted-at buffer (same shader attribute name).
    beaconGeometry.setAttribute('aPolePlantedAt', aBeaconPlantedAt);

    const flagLighting = buildLightingUniforms(shadowFromSun.direction);
    const poleLighting = buildLightingUniforms(shadowFromSun.direction);

    // Bind the real atlas at construction time when available — swapping
    // `uAtlas.value` post-construction can leave the renderer pointing at the
    // already-uploaded placeholder until something forces a re-upload.
    const initialAtlasTexture = flagAtlas?.texture ?? createPlaceholderAtlasTexture();
    const initialAtlasGrid = flagAtlas
        ? new THREE.Vector2(flagAtlas.gridCols, flagAtlas.gridRows)
        : ATLAS_DEFAULT_GRID.clone();
    const initialCellPadding = flagAtlas
        ? new THREE.Vector2(flagAtlas.cellPaddingPx / 256, flagAtlas.cellPaddingPx / 170)
        : ATLAS_DEFAULT_PADDING_UV.clone();

    const flagMaterial = new THREE.ShaderMaterial({
        uniforms: {
            uTime: { value: 0 },
            uFlutterIntensity: { value: 0.6 },
            uBurstDecaySeconds: { value: BURST_DECAY_SECONDS },
            uBurstAmplitudeMult: { value: BURST_AMPLITUDE_MULT },
            uAmbientFreqX: { value: AMBIENT_FREQ_X },
            uAmbientFreqTime: { value: AMBIENT_FREQ_TIME },
            uAmbientPhaseScatter: { value: AMBIENT_PHASE_SCATTER },
            uFlutterBaseAmplitude: { value: FLUTTER_BASE_AMPLITUDE },
            uAtlas:        { value: initialAtlasTexture },
            uAtlasGrid:    { value: initialAtlasGrid },
            uHasAtlas:     { value: flagAtlas ? 1.0 : 0.0 },
            uCellPadding:  { value: initialCellPadding },
            uFabricStrength: { value: 0.075 },
            uSeamStrength: { value: 0.34 },
            uFoldShadeStrength: { value: 0.18 },
            ...flagLighting,
        },
        vertexShader: FLAG_VERTEX_SHADER,
        fragmentShader: FLAG_FRAGMENT_SHADER,
        side: THREE.DoubleSide,
    });
    const poleMaterial = new THREE.ShaderMaterial({
        uniforms: {
            ...poleLighting,
            uPoleWhite: { value: new THREE.Color(0xf8fbff) },
            uPoleShade: { value: new THREE.Color(0xd8e1e8) },
            uPoleHighlight: { value: new THREE.Color(0xffffff) },
            uSpecularStrength: { value: 0.42 },
            uRimStrength: { value: 0.22 },
            uPoleHeight: { value: POLE_HEIGHT },
            uTime: { value: 0 },
            uFreshGlowColor: { value: new THREE.Color(FRESH_GLOW_COLOR[0], FRESH_GLOW_COLOR[1], FRESH_GLOW_COLOR[2]) },
            uFreshGlowWindow: { value: freshGlowWindowSeconds },
            uFreshGlowPulseSpeed: { value: FRESH_GLOW_PULSE_SPEED },
            uFreshGlowStrength: { value: FRESH_GLOW_STRENGTH },
        },
        vertexShader: POLE_VERTEX_SHADER,
        fragmentShader: POLE_FRAGMENT_SHADER,
    });
    const shadowMaterial = new THREE.ShaderMaterial({
        uniforms: {
            uShadowOpacity: { value: shadowFromSun.opacity },
        },
        vertexShader: SHADOW_VERTEX_SHADER,
        fragmentShader: SHADOW_FRAGMENT_SHADER,
        transparent: true,
        depthWrite: false,
        depthTest: true,
        side: THREE.DoubleSide,
    });

    const beaconMaterial = new THREE.ShaderMaterial({
        uniforms: {
            uTime: { value: 0 },
            uBeaconHeight: { value: BEACON_HEIGHT },
            uBeaconWidth: { value: BEACON_WIDTH },
            uBeaconColor: { value: new THREE.Color(FRESH_GLOW_COLOR[0], FRESH_GLOW_COLOR[1], FRESH_GLOW_COLOR[2]) },
            uFreshGlowWindow: { value: freshGlowWindowSeconds },
            uBeaconPulseSpeed: { value: BEACON_PULSE_SPEED },
        },
        vertexShader: BEACON_VERTEX_SHADER,
        fragmentShader: BEACON_FRAGMENT_SHADER,
        transparent: true,
        depthWrite: false,
        depthTest: true,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
        side: THREE.DoubleSide,
    });

    const flagMesh = new THREE.InstancedMesh(flagGeometry, flagMaterial, maxInstances);
    const poleMesh = new THREE.InstancedMesh(poleGeometry, poleMaterial, maxInstances);
    const shadowMesh = new THREE.InstancedMesh(shadowGeometry, shadowMaterial, maxInstances);
    const beaconMesh = new THREE.InstancedMesh(beaconGeometry, beaconMaterial, maxInstances);
    flagMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    poleMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    shadowMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    beaconMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    flagMesh.count = 0;
    poleMesh.count = 0;
    shadowMesh.count = 0;
    beaconMesh.count = 0;
    // Frustum-culling per pennant is overkill for instanced rendering of small objects;
    // we manage culling at the scene level instead.
    flagMesh.frustumCulled = false;
    poleMesh.frustumCulled = false;
    shadowMesh.frustumCulled = false;
    beaconMesh.frustumCulled = false;
    shadowMesh.renderOrder = 1;
    beaconMesh.renderOrder = 3; // additive, drawn after the opaque pennants

    return {
        flagMesh,
        poleMesh,
        shadowMesh,
        beaconMesh,
        attributes: { aPrimary, aSecondary, aPlantedAt, aFlagIndex, aPhase, aSwingBoost, aPolePlantedAt },
        // Beacon compaction state: its own planted-at buffer, the glow window, and
        // rebuild throttling. beaconMesh.count is driven by rebuildBeacons, not nextIndex.
        beaconPlantedAt: aBeaconPlantedAt,
        freshGlowWindowSeconds,
        beaconDirty: true,
        lastBeaconRebuild: -Infinity,
        flagAtlas,
        idIndex: new Map(), // id → instance index
        // Render-cap eviction state. `order` holds live ids oldest-first; `pinned`
        // holds ids that must never be evicted (the local user's own plants).
        order: [],
        pinned: new Set(),
        instanceData: new Array(maxInstances),
        sunDirection: shadowFromSun.direction.clone(),
        shadowYaw: shadowFromSun.yaw,
        shadowLengthScale: shadowFromSun.lengthScale,
        shadowWidthScale: shadowFromSun.widthScale,
        leadingTeamCode: null,
        leadingTeamHeightScale: LEADING_TEAM_HEIGHT_SCALE,
        teamHeightScales: new Map(),
        nextIndex: 0,
        maxInstances,
    };
}

const tmpMatrix = new THREE.Matrix4();
const tmpMatrix2 = new THREE.Matrix4();
const tmpQuat = new THREE.Quaternion();
const tmpQuat2 = new THREE.Quaternion();
const tmpEuler = new THREE.Euler();
const tmpPos = new THREE.Vector3();
const tmpVec = new THREE.Vector3();
const tmpScale = new THREE.Vector3(1, 1, 1);

function normalizedTeamCode(teamCode) {
    return typeof teamCode === 'string' ? teamCode.toUpperCase() : null;
}

function heightScaleForTeam(system, teamCode) {
    return teamCode ? (system.teamHeightScales?.get(teamCode) ?? 1) : 1;
}

function effectiveHeightScale(system, data) {
    const team = heightScaleForTeam(system, data.teamCode);
    const pole = Number.isFinite(data.poleHeightScale) ? data.poleHeightScale : 1;
    return team * pole;
}

export function composePennantMatrix({ x, y, z, rotation, heightScale = 1, swingAngle = 0 }) {
    tmpPos.set(x, y, z);
    // Pole stays vertical and at the team's preconfigured height — fan effort
    // raises the flag along the pole rather than growing the pole itself.
    tmpEuler.set(0, rotation, 0);
    tmpQuat.setFromEuler(tmpEuler);
    tmpScale.set(1, heightScale, 1);
    tmpMatrix.compose(tmpPos, tmpQuat, tmpScale);
    return tmpMatrix;
}

// Retained as a constant-1 helper so old callers compile; the per-instance
// flagWidthScale field on instance data is always 1 under the pole-only-growth
// model. Width never couples to pole height anymore.
export function flagWidthScaleForHeight(_heightScale = 1) {
    return 1;
}

export function composeFlagMatrix({ x, y, z, rotation, heightScale = 1, flagWidthScale = 1, flagHoistFraction = 1, swingAngle = 0, flagSwingOffset = 0 }) {
    // Flag is rigid in world units — its cloth dimensions never scale with the
    // pole's heightScale. The flag rides on top of whatever pole exists: its
    // top edge always sits at poleTop - FLAG_FINIAL_CLEARANCE. As the pole
    // grows during the minigame, the pole-top rises and the flag follows.
    const poleTop = y + POLE_HEIGHT * heightScale;
    const flagTopY = poleTop - FLAG_FINIAL_CLEARANCE;
    const flagBottomY = flagTopY - FLAG_HEIGHT;

    tmpEuler.set(0, rotation, 0);
    tmpQuat.setFromEuler(tmpEuler);

    // Identity scale: flag width and height are constant world units. Offset
    // so the flag's bottom sits at local y=0, then rotate around the pole
    // axis, then translate to (x, flagBottomY, z).
    tmpMatrix.makeScale(1, 1, 1);

    tmpMatrix2.makeTranslation(0, -FLAG_BOTTOM_Y, 0);
    tmpMatrix.premultiply(tmpMatrix2);

    tmpMatrix2.makeRotationFromQuaternion(tmpQuat);
    tmpMatrix.premultiply(tmpMatrix2);

    tmpMatrix2.makeTranslation(x, flagBottomY, z);
    tmpMatrix.premultiply(tmpMatrix2);

    return tmpMatrix;
}

function composeShadowMatrix({ x, y, z, heightScale = 1 }, system) {
    tmpPos.set(x, y, z);
    tmpEuler.set(0, system.shadowYaw ?? 0, 0);
    tmpQuat.setFromEuler(tmpEuler);
    tmpScale.set(
        (system.shadowLengthScale ?? 1) * heightScale,
        1,
        (system.shadowWidthScale ?? 1) * (0.9 + heightScale * 0.1),
    );
    tmpMatrix.compose(tmpPos, tmpQuat, tmpScale);
    return tmpMatrix;
}

export function normalizeShaderPlantedAt(
    plantedAt,
    {
        nowSeconds = performance.now() / 1000,
        epochNowSeconds = Date.now() / 1000,
        maxAgeSeconds = PRELOADED_PENNANT_MAX_SHADER_AGE_SECONDS,
    } = {},
) {
    if (!Number.isFinite(plantedAt)) return nowSeconds;
    if (plantedAt > nowSeconds + 60) {
        const ageSeconds = Math.max(0, epochNowSeconds - plantedAt);
        return nowSeconds - Math.min(ageSeconds, maxAgeSeconds);
    }
    return plantedAt;
}

// Free the oldest non-pinned instance slot so a new plant can reuse it. Scans the
// recency order oldest-first, skipping pinned ids (the local user's own plants)
// and self-healing any stale ids left in `order` by an out-of-band idIndex edit.
// Returns the recycled slot index, or -1 if every live instance is pinned.
function evictOldestNonPinned(system) {
    let k = 0;
    while (k < system.order.length) {
        const oldId = system.order[k];
        const slot = system.idIndex.get(oldId);
        if (slot === undefined) {
            // stale bookkeeping (e.g. a preview id renamed on commit) — drop it.
            system.order.splice(k, 1);
            system.pinned.delete(oldId);
            continue;
        }
        if (system.pinned.has(oldId)) {
            k++; // pinned — never evict; keep scanning for an older non-pinned one
            continue;
        }
        system.order.splice(k, 1);
        system.idIndex.delete(oldId);
        return slot;
    }
    return -1;
}

/**
 * Plant a pennant. Sets per-instance matrix + colors + plantedAt timestamp.
 * `pinned` marks the local user's own plant so the render cap never evicts it.
 * @returns the instance index assigned (or -1 if full and every slot is pinned).
 */
export function addPennant(system, { id, x, y = 0, z, rotation = 0, primary, secondary, plantedAt = performance.now() / 1000, flagIndex = 0, phase = null, teamCode = null, swingBoost = 0, flagHoistFraction = 1, poleHeightScale = 1, ageSeconds = 0, pinned = false }) {
    if (system.idIndex.has(id)) {
        return system.idIndex.get(id);
    }
    let i;
    if (system.nextIndex < system.maxInstances) {
        i = system.nextIndex;
        system.nextIndex++;
    } else {
        // Buffer full — recycle the oldest non-pinned slot instead of dropping.
        i = evictOldestNonPinned(system);
        if (i === -1) return -1;
    }
    system.idIndex.set(id, i);
    system.order.push(id);
    if (pinned) system.pinned.add(id);

    const code = normalizedTeamCode(teamCode);
    const shaderPlantedAt = normalizeShaderPlantedAt(plantedAt);
    // Un-clamped planted-at (load-time seconds) for the pole's fresh glow: the
    // true wall-clock age, so a pole created `ageSeconds` ago reads as that old
    // and glows for the remainder of its 30-min window.
    const realAgeSeconds = Number.isFinite(ageSeconds) ? Math.max(0, ageSeconds) : 0;
    const polePlantedAt = (performance.now() / 1000) - realAgeSeconds;
    const shaderPhase = Number.isFinite(phase) ? phase : i * 0.61803398875;
    const teamScale = heightScaleForTeam(system, code);
    const polScale = Number.isFinite(poleHeightScale) ? poleHeightScale : 1;
    const heightScale = teamScale * polScale;
    // flagWidthScale is permanently 1 under the pole-only-growth model — the
    // cloth never widens with pole height. Field is kept on instance data so
    // any external read (tests, shaders) stays compatible.
    // polePlantedAt is stored on instance data so the beacon gate can select the
    // currently-fresh poles (see selectFreshBeaconInstances / rebuildBeacons).
    system.instanceData[i] = { x, y, z, rotation, teamCode: code, heightScale, poleHeightScale: polScale, flagWidthScale: 1, flagHoistFraction, swingAngle: 0, flagSwingOffset: 0, polePlantedAt };

    composeFlagMatrix(system.instanceData[i]);
    system.flagMesh.setMatrixAt(i, tmpMatrix);
    composePennantMatrix(system.instanceData[i]);
    system.poleMesh.setMatrixAt(i, tmpMatrix);
    composeShadowMatrix(system.instanceData[i], system);
    system.shadowMesh.setMatrixAt(i, tmpMatrix);
    // The beacon is drawn only for fresh poles and is rebuilt (compacted) by
    // rebuildBeacons — mark it dirty rather than writing a per-pole instance here.
    system.beaconDirty = true;

    system.attributes.aPrimary.array.set(primary, i * 3);
    system.attributes.aSecondary.array.set(secondary, i * 3);
    system.attributes.aPlantedAt.array[i] = shaderPlantedAt;
    system.attributes.aFlagIndex.array[i] = flagIndex;
    system.attributes.aPhase.array[i] = shaderPhase;
    system.attributes.aSwingBoost.array[i] = swingBoost;
    system.attributes.aPolePlantedAt.array[i] = polePlantedAt;

    system.attributes.aPrimary.needsUpdate = true;
    system.attributes.aSecondary.needsUpdate = true;
    system.attributes.aPlantedAt.needsUpdate = true;
    system.attributes.aFlagIndex.needsUpdate = true;
    system.attributes.aPhase.needsUpdate = true;
    system.attributes.aSwingBoost.needsUpdate = true;
    system.attributes.aPolePlantedAt.needsUpdate = true;
    system.flagMesh.instanceMatrix.needsUpdate = true;
    system.poleMesh.instanceMatrix.needsUpdate = true;
    system.shadowMesh.instanceMatrix.needsUpdate = true;

    system.flagMesh.count = system.nextIndex;
    system.poleMesh.count = system.nextIndex;
    system.shadowMesh.count = system.nextIndex;
    // beaconMesh.count is set by rebuildBeacons (fresh poles only), not here.

    return i;
}

export function setLeadingTeamHeight(system, teamCode, heightScale = LEADING_TEAM_HEIGHT_SCALE) {
    return setTeamHeightTiers(system, teamCode ? [{ code: teamCode, scale: heightScale }] : []);
}

export function setTeamHeightTiers(system, tiers = []) {
    const nextScales = new Map();
    for (const tier of tiers) {
        const code = normalizedTeamCode(tier?.code ?? tier?.teamCode);
        if (!code) continue;
        const scale = Math.max(1, Number.isFinite(tier.scale) ? tier.scale : 1);
        if (scale > 1) nextScales.set(code, scale);
    }

    const currentScales = system.teamHeightScales ?? new Map();
    const unchanged = currentScales.size === nextScales.size
        && [...nextScales].every(([code, scale]) => currentScales.get(code) === scale);
    if (unchanged) {
        return 0;
    }

    system.teamHeightScales = nextScales;
    system.leadingTeamCode = [...nextScales.keys()][0] ?? null;
    system.leadingTeamHeightScale = [...nextScales.values()][0] ?? LEADING_TEAM_HEIGHT_SCALE;

    let updated = 0;
    for (let i = 0; i < system.nextIndex; i++) {
        const data = system.instanceData[i];
        if (!data) continue;
        const nextScale = effectiveHeightScale(system, data);
        if (data.heightScale === nextScale) continue;

        data.heightScale = nextScale;
        // flagWidthScale stays at 1 — pole grows, flag doesn't.
        composeFlagMatrix(data);
        system.flagMesh.setMatrixAt(i, tmpMatrix);
        composePennantMatrix(data);
        system.poleMesh.setMatrixAt(i, tmpMatrix);
        composeShadowMatrix(data, system);
        system.shadowMesh.setMatrixAt(i, tmpMatrix);
        updated++;
    }

    if (updated > 0) {
        system.flagMesh.instanceMatrix.needsUpdate = true;
        system.poleMesh.instanceMatrix.needsUpdate = true;
        system.shadowMesh.instanceMatrix.needsUpdate = true;
    }

    return updated;
}

export function buildTeamHeightTiers(rankedTeams = [], scales = TEAM_HEIGHT_TIER_SCALES) {
    if (!Array.isArray(rankedTeams)) return [];
    return rankedTeams
        .slice(0, scales.length)
        .map((team, index) => ({
            code: normalizedTeamCode(team?.code ?? team?.teamCode),
            scale: scales[index],
        }))
        .filter((tier) => tier.code && Number.isFinite(tier.scale) && tier.scale > 1);
}

export function setPennantSunDirection(system, sunDirection) {
    const shadowFromSun = deriveShadowFromSun(sunDirection);
    system.sunDirection.copy(shadowFromSun.direction);
    system.shadowYaw = shadowFromSun.yaw;
    system.shadowLengthScale = shadowFromSun.lengthScale;
    system.shadowWidthScale = shadowFromSun.widthScale;

    system.flagMesh.material.uniforms.uSunDirection.value.copy(shadowFromSun.direction);
    system.poleMesh.material.uniforms.uSunDirection.value.copy(shadowFromSun.direction);
    system.shadowMesh.material.uniforms.uShadowOpacity.value = shadowFromSun.opacity;

    for (let i = 0; i < system.nextIndex; i++) {
        const data = system.instanceData[i];
        if (!data) continue;
        composeShadowMatrix(data, system);
        system.shadowMesh.setMatrixAt(i, tmpMatrix);
    }

    system.shadowMesh.instanceMatrix.needsUpdate = true;
    return system.nextIndex;
}

// How often the beacon fresh-set is recomputed (seconds). The glow window is
// minutes long, so poles age out slowly — a coarse cadence keeps the CPU scan
// rare while the shader still animates each fresh beacon's fade smoothly.
const BEACON_REBUILD_INTERVAL_SECONDS = 0.5;

// Indices of poles still inside the fresh-glow window, packed (oldest slot first).
// Pure so it can be unit-tested; drives which beacons actually get drawn.
export function selectFreshBeaconInstances(instanceData, nowSeconds, windowSeconds) {
    const fresh = [];
    for (let i = 0; i < instanceData.length; i++) {
        const d = instanceData[i];
        if (!d) continue;
        const age = nowSeconds - d.polePlantedAt;
        if (age >= 0 && age < windowSeconds) fresh.push(i);
    }
    return fresh;
}

// Compact the beacon InstancedMesh down to just the currently-fresh poles and set
// beaconMesh.count accordingly, so aged beacons stop rasterizing their large
// additive quads. Returns the number of beacons drawn.
export function rebuildBeacons(system, nowSeconds) {
    const windowSeconds = system.freshGlowWindowSeconds ?? FRESH_GLOW_WINDOW_SECONDS;
    const fresh = selectFreshBeaconInstances(system.instanceData, nowSeconds, windowSeconds);
    const plantedAt = system.beaconPlantedAt.array;
    for (let k = 0; k < fresh.length; k++) {
        const d = system.instanceData[fresh[k]];
        tmpMatrix.makeTranslation(d.x, d.y, d.z);
        system.beaconMesh.setMatrixAt(k, tmpMatrix);
        plantedAt[k] = d.polePlantedAt;
    }
    system.beaconMesh.count = fresh.length;
    system.beaconMesh.instanceMatrix.needsUpdate = true;
    system.beaconPlantedAt.needsUpdate = true;
    system.beaconDirty = false;
    system.lastBeaconRebuild = nowSeconds;
    return fresh.length;
}

/**
 * Drive the flutter shader. Call once per frame from the render loop with the
 * elapsed seconds since page load. Drives the flag flutter, the pole's
 * fresh-glow pulse/age, and the beacon pulse — and periodically recompacts the
 * beacon draw set to only the fresh poles.
 */
export function updateFlutter(system, timeSeconds) {
    system.flagMesh.material.uniforms.uTime.value = timeSeconds;
    system.poleMesh.material.uniforms.uTime.value = timeSeconds;
    system.beaconMesh.material.uniforms.uTime.value = timeSeconds;
    if (system.beaconDirty || timeSeconds - system.lastBeaconRebuild >= BEACON_REBUILD_INTERVAL_SECONDS) {
        rebuildBeacons(system, timeSeconds);
    }
}

/**
 * Tune the ambient flutter amplitude — read from `config.pennants.flutterIntensity`
 * at app boot, exposed as a setter so config edits without page reload also work.
 */
export function setFlutterIntensity(system, intensity) {
    system.flagMesh.material.uniforms.uFlutterIntensity.value = intensity;
}

/**
 * Retune the fresh-pole glow + sky-beacon window (seconds) at runtime. Both the
 * pole and beacon shaders fade over this window, so one setter drives both.
 * Used by the demo's live glow-duration control. Returns the applied value.
 */
export function setFreshGlowWindow(system, seconds) {
    const applied = Math.max(0.1, Number.isFinite(seconds) ? seconds : FRESH_GLOW_WINDOW_SECONDS);
    system.poleMesh.material.uniforms.uFreshGlowWindow.value = applied;
    system.beaconMesh.material.uniforms.uFreshGlowWindow.value = applied;
    // The beacon gate keys off this window too — recompute the fresh set next frame.
    system.freshGlowWindowSeconds = applied;
    system.beaconDirty = true;
    return applied;
}

export function setPennantSwingBoost(system, index, boost) {
    if (index < 0 || index >= system.maxInstances) return;
    system.attributes.aSwingBoost.array[index] = boost;
    system.attributes.aSwingBoost.needsUpdate = true;
}

/**
 * Set the per-pennant pole-height scale (multiplied on top of the team's
 * tier-rank scale). The minigame drives this smoothly while the user pulls,
 * then snaps it to a tier-specific value on commit. Returns true when the
 * scale changed and matrices were re-composed.
 */
export function setPennantPoleHeightScale(system, index, poleScale) {
    if (index < 0 || index >= system.nextIndex) return false;
    const data = system.instanceData[index];
    if (!data) return false;
    const next = Math.max(0.1, Number.isFinite(poleScale) ? poleScale : 1);
    data.poleHeightScale = next;
    data.heightScale = effectiveHeightScale(system, data);
    // flagWidthScale stays at 1 under the pole-only-growth model.
    return true;
}

/**
 * Update matrices for a single pennant instance. Use when flagHoistFraction or
 * swingAngle changes for an existing pennant (e.g. during preview growth).
 */
export function updatePennantMatrices(system, index) {
    const data = system.instanceData[index];
    if (!data) return;
    composeFlagMatrix(data);
    system.flagMesh.setMatrixAt(index, tmpMatrix);
    composePennantMatrix(data);
    system.poleMesh.setMatrixAt(index, tmpMatrix);
    composeShadowMatrix(data, system);
    system.shadowMesh.setMatrixAt(index, tmpMatrix);
    system.flagMesh.instanceMatrix.needsUpdate = true;
    system.poleMesh.instanceMatrix.needsUpdate = true;
    system.shadowMesh.instanceMatrix.needsUpdate = true;
    // Beacon anchor is unchanged by height/hoist tweaks; the gate handles it.
}

/**
 * Hide a pennant by scaling its matrices to zero. Used for cancelling a preview.
 */
export function hidePennant(system, index) {
    if (index < 0 || index >= system.maxInstances) return;
    tmpMatrix.makeScale(0, 0, 0);
    system.flagMesh.setMatrixAt(index, tmpMatrix);
    system.poleMesh.setMatrixAt(index, tmpMatrix);
    system.shadowMesh.setMatrixAt(index, tmpMatrix);
    system.flagMesh.instanceMatrix.needsUpdate = true;
    system.poleMesh.instanceMatrix.needsUpdate = true;
    system.shadowMesh.instanceMatrix.needsUpdate = true;
    if (system.instanceData[index]) system.instanceData[index].polePlantedAt = -Infinity; // never fresh
    system.beaconDirty = true;
}

export function getPennantCount(system) {
    return system.idIndex.size;
}

/**
 * Rename a live pennant's id in place, keeping the slot and all render-cap
 * bookkeeping (recency order + pinned set) consistent. Used when an optimistic
 * preview id is replaced by the server-assigned id on commit.
 * @returns true if the old id existed and was renamed.
 */
export function renamePennantId(system, oldId, newId) {
    if (!system.idIndex.has(oldId)) return false;
    const slot = system.idIndex.get(oldId);
    system.idIndex.delete(oldId);
    system.idIndex.set(newId, slot);
    const k = system.order.indexOf(oldId);
    if (k !== -1) system.order[k] = newId;
    if (system.pinned.has(oldId)) {
        system.pinned.delete(oldId);
        system.pinned.add(newId);
    }
    return true;
}

export function disposePennantSystem(system) {
    system.flagMesh.geometry.dispose();
    system.flagMesh.material.dispose();
    system.poleMesh.geometry.dispose();
    system.poleMesh.material.dispose();
    system.shadowMesh.geometry.dispose();
    system.shadowMesh.material.dispose();
    system.beaconMesh.geometry.dispose();
    system.beaconMesh.material.dispose();
    if (system.flagAtlas?.texture) {
        system.flagAtlas.texture.dispose();
    }
    system.idIndex.clear();
    system.instanceData.fill(null);
    system.nextIndex = 0;
}
